import type {ApiError} from '@/redux/apiError';
import type {
  CrossBordersData,
  DiscountType,
  InviaType,
  Meta,
  Price,
  Room,
  Sponsored,
} from '@/redux/v1/models';
import type {ISO8601Date} from '@/types';

// backend
// -------

export type TravelDateInfo = {
  startDate: string;
  endDate: string;
  /**
   * @deprecated
   */
  duration: number;
  stays: number;
  overnightStays: number;
};

export type TourOperator = {
  code: string;
  name: string;
  image: string;
  travelType?: string;
};

export type PricePax = {
  type: 'ADULT' | 'CHILD' | 'ALL';
  /**
   * @deprecated use Pay instead
   */
  value: number;
  Pay: Price;
  Display: Price;
};

type Airline = {
  code: string;
  name: string;
};

export type Airport = {
  iata: string;
  name: string;
  city: string;
};

type Luggage = {
  weight: number;
  dimension: string;
};

export type FlightSegment = {
  departure: {
    airport: Airport;
    date: ISO8601Date;
  };
  arrival: {
    airport: Airport;
    date: ISO8601Date;
  };
  airline: Airline;
  duration: number;
  changeOfDate: number;
  cabinClass: string;
  flightNumber: string;
};

export type FlightRoute = {
  departure: {
    airport: Airport;
    date: ISO8601Date;
  };
  arrival: {
    airport: Airport;
    date: ISO8601Date;
  };
  duration: number;
  changeOfDate: number;
  averageFlightDuration: number;
  numberOfStops: number;
  directFlightAssumed: boolean;
  cabinClass: string;
  airline: Airline;
  luggage?: Luggage;
  handLuggage?: Luggage;
  segments?: null | Array<FlightSegment>;
};

export type Flight = {
  outbound?: FlightRoute;
  inbound?: FlightRoute;
};

export type RawTourOperatorSpecial = {
  type: string;
  text: string;
  hint: string;
  hintMarkdown?: string;
  totalDiscount?: number;
  totalFee?: number;
  totalFeeDisplay?: Price;
  dateUntil?: ISO8601Date;
};

type RawTourOperatorSpecials = {
  flex: Array<RawTourOperatorSpecial>;
  specials: Array<RawTourOperatorSpecial>;
  safety: Array<RawTourOperatorSpecial>;
};

export const CANCELLATION_RULE_TYPE_FEE = 'IT08F-CAN#FEE#FEE';
export const CANCELLATION_RULE_TYPE_FEE_INCLUDED = 'IT08F-CAN#FEE#FEIN';
export const CANCELLATION_RULE_TYPE_FREE = 'IT08F-CAN#FREE';

export const RESERVATION_RULE_TYPE_INCLUSIVE = 'IT08F-RES#INC';
export const RESERVATION_RULE_TYPE_EXCLUSIVE = 'IT08F-RES#EXC';

export const CANCELLATION_RULE_TYPE_EXPEDIA_NOT_REFUNDABLE = 'EPS#REF#NONE';
export const CANCELLATION_RULE_TYPE_EXPEDIA_PARTIAL_REFUNDABLE = 'EPS#REF';
export const CANCELLATION_RULE_TYPE_EXPEDIA_REFUNDABLE = 'EPS#REF#FULL';

type AllRuleTypes =
  | typeof RESERVATION_RULE_TYPE_INCLUSIVE
  | typeof RESERVATION_RULE_TYPE_EXCLUSIVE
  | typeof CANCELLATION_RULE_TYPE_EXPEDIA_NOT_REFUNDABLE
  | typeof CANCELLATION_RULE_TYPE_EXPEDIA_PARTIAL_REFUNDABLE
  | typeof CANCELLATION_RULE_TYPE_EXPEDIA_REFUNDABLE
  | typeof CANCELLATION_RULE_TYPE_FEE
  | typeof CANCELLATION_RULE_TYPE_FEE_INCLUDED
  | typeof CANCELLATION_RULE_TYPE_FREE;

type RawCancellationRule = {
  price: number;
  tmpPrice: Price;
  priceDisplay: Price;
  ruleType: AllRuleTypes;
  validTo: ISO8601Date;
};

export type RawCancellationRules = null | Array<RawCancellationRule>;

// @TODO mark all as optional to be safe?
export type RawOffer = {
  id: string;
  travelDateInfo: TravelDateInfo;
  tourOperator: TourOperator;
  rooms: Array<Room>;
  serviceCode: string;
  supplier: string;
  prices: Array<PricePax>;
  /**
   * @deprecated use tmpTotalPrice instead
   */
  totalPrice: PricePax;
  tmpTotalPrice: Price;
  totalPriceDisplay: Price;
  /**
   * @deprecated use tmpTotalPriceOriginal instead
   */
  totalPriceOriginal: number;
  tmpTotalPriceOriginal: Price;
  totalPriceOriginalDisplay: Price;
  /**
   * @deprecated use tmpTotalDiscount instead
   */
  totalDiscount: number;
  discountType: DiscountType;
  discountPercentage: number;
  tmpTotalDiscount: Price;
  totalDiscountDisplay: Price;
  availabilityData: string;
  transportation?: null | Array<InviaType>;
  flight: Flight;
  tourOperatorSpecials?: RawTourOperatorSpecials;
  cancellationRules: RawCancellationRules;
  alternativeFlightsAvailable: boolean;
  sponsored?: Sponsored;
};

export const BEST_OFFER_TYPE_CHEAPEST = 'CHEAPEST' as const;
export const BEST_OFFER_TYPE_BESTVALUE = 'BESTVALUE' as const;
export const BEST_OFFER_TYPE_PREMIUM = 'PREMIUM' as const;
export const BEST_OFFER_TYPE_SPONSORED = 'SPONSORED' as const;

type BestOfferType =
  | typeof BEST_OFFER_TYPE_CHEAPEST
  | typeof BEST_OFFER_TYPE_BESTVALUE
  | typeof BEST_OFFER_TYPE_PREMIUM
  | typeof BEST_OFFER_TYPE_SPONSORED;

export type RawBestOffer = {
  offerID: string;
  type: BestOfferType;
  upgrades: Array<string> | null;
};

export type BestOffer = {
  offerID: string;
  availabilityData: string;
  type: BestOfferType;
  upgrades: Array<string> | null;
  sponsored: Sponsored | null;
};

export type OffersResponse = {
  count: number;
  offers: Array<RawOffer>;
  meta?: Meta;
  initialMeta?: Meta;
  translations: Record<string, string>;
  bestOffers?: Array<RawBestOffer>;
  offerCode?: string;
  crossBorders?: CrossBordersData;
};

// frontend
// --------

export type CancellationRule = Omit<RawCancellationRule, 'tmpPrice'>;
export type CancellationRules = Array<CancellationRule>;

// eslint-disable-next-line sonarjs/redundant-type-aliases -- not ideal but wanted
export type TourOperatorSpecial = RawTourOperatorSpecial;
// eslint-disable-next-line sonarjs/redundant-type-aliases -- not ideal but wanted
export type TourOperatorSpecials = RawTourOperatorSpecials;

export type Offer = {
  id: string;
  travelDateInfo: TravelDateInfo;
  tourOperator: TourOperator;
  rooms: Array<Room>;
  cancellationRules: CancellationRules;
  serviceCode: string;
  supplier: string;
  prices: Array<PricePax>;
  totalPrice: Price;
  totalPriceDisplay: Price;
  totalPriceOriginal: Price;
  totalPriceOriginalDisplay: Price;
  totalDiscount: Price;
  totalDiscountDisplay: Price;
  availabilityData: string;
  transportation: Array<InviaType>;
  flight: Flight;
  tourOperatorSpecials: TourOperatorSpecials;
  discountType: DiscountType;
  discountPercentage: number;
  alternativeFlightsAvailable: boolean;
  sponsored: Sponsored | null;
};

export type Offers = {
  count: number;
  offers: Array<Offer>;
  metaUnfiltered?: null | Meta;
  metaFiltered?: null | Meta;
  translations: Record<string, string>;
  bestOffers: Array<BestOffer>;
  offerCode?: string;
  crossBorders?: CrossBordersData;
};

// state
// -----

export type StateByHotelId = Readonly<{
  byId?: Record<string, Offer>;
  ids?: Array<string>;
  count?: number;
  metaUnfiltered?: null | Meta;
  metaFiltered?: null | Meta;
  translations?: Record<string, string>;
  bestOffers: Array<BestOffer>;
  offerCode?: string;
}>;

type RequestMeta = {id: string};

export type State = Readonly<{
  loading: Record<string, boolean>;
  requests: Record<string, RequestMeta>;
  byHotelId: Record<string, StateByHotelId>;
  errors: Record<string, ApiError>;
  crossBorders?: CrossBordersData;
}>;

export type {Meta} from '@/redux/v1/models';
